import { MEGA_MENU_SECTION_DEFS, type IMegaMenuLinksBySection, type IMegaMenuTreeNode } from '@/modules/adminModule/types/megaMenuTree'

// GTM tagging / car-search filters / "always show as expandable" are deliberately NOT part of
// the CMS content model (see discussion - admins only ever edit title/icon/link). They're kept
// here, in frontend code only, keyed by the link's stable CMS id (set when the link was created
// in /panel/mega-menu - either picked from a CMS subpage/location, or typed by hand).
interface IMegaMenuLinkMeta {
	category?: string
	rentalLength?: string
	filters?: string[]
	forceExpandable?: boolean
}

const MEGA_MENU_LINK_META: Record<string, IMegaMenuLinkMeta> = {
	'short-term-rentals': { rentalLength: 'short_term' },
	'medium-term-rental': { rentalLength: 'medium_term' },
	'long-term-rental': { rentalLength: 'long_term' },
	// forceExpandable is required on all 4 CUSTOM_KEYS categories, not just cities/airports:
	// MegaMenu.vue clears the hovered category immediately (before areCustomItems/dynamic
	// injection ever runs) whenever items.length === 0 and menu isn't true. bus-rental/
	// delivery-van-rental need this too, or the dynamic city list silently never appears
	// while they have zero (or not-yet-added) static sublinks.
	'bus-rental': { category: 'bus', filters: ['van'], forceExpandable: true },
	'delivery-van-rental': { category: 'delivery', filters: ['delivery'], forceExpandable: true },
	cities: { category: 'city', forceExpandable: true },
	airports: { category: 'airport', forceExpandable: true },
}

const localizedTitle = (node: { title_pl: string; title_en: string }, locale: string) => (locale === 'pl' ? node.title_pl : node.title_en)

const mapSublink = (node: IMegaMenuTreeNode, locale: string): IMegaMenuItem => ({
	title: localizedTitle(node, locale),
	translation_key_title: node.id,
	link: node.link ?? undefined,
})

const mapLink = (node: IMegaMenuTreeNode, locale: string): IMegaMenuItem => {
	const meta = MEGA_MENU_LINK_META[node.id]
	return {
		title: localizedTitle(node, locale),
		translation_key_title: node.id,
		subText: (locale === 'pl' ? node.subtext_pl : node.subtext_en) ?? undefined,
		icon: node.icon ?? undefined,
		link: node.link ?? undefined,
		category: meta?.category,
		rental_length: meta?.rentalLength,
		filters: meta?.filters,
		menu: meta?.forceExpandable ?? false,
		items: node.children?.map((child) => mapSublink(child, locale)),
	}
}

export const mapMegaMenuContent = (linksBySection: IMegaMenuLinksBySection, locale: string): IMegaMenuItem[] =>
	MEGA_MENU_SECTION_DEFS.map((def) => ({
		title: localizedTitle(def, locale),
		translation_key_title: def.id,
		items: (linksBySection[def.id] ?? []).map((link) => mapLink(link, locale)),
	}))
